import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Database, Trash2, Settings, ArrowRight, X, Layers, MessageSquare } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { instancesApi, llmApi } from '../api';
import { Card, Button, Badge, Input, Select, Spinner, EmptyState, PageHeader, ModalOverlay } from '../components/UI';

const EMBEDDING_MODELS = {
  openai: ['text-embedding-3-small', 'text-embedding-3-large', 'text-embedding-ada-002'],
  ollama: ['nomic-embed-text', 'mxbai-embed-large', 'all-minilm']
};

function InstanceModal({ onClose, onSave }) {
  const { t } = useTranslation(['instances', 'common']);
  const { data: providers } = useQuery({ queryKey: ['providers'], queryFn: llmApi.providers });

  const [form, setForm] = useState({
    name: '', description: '',
    embeddingProvider: 'openai', embeddingModel: 'text-embedding-3-small',
    qaProvider: 'openai', qaModel: 'gpt-4o',
    chunkSize: 1000, chunkOverlap: 200, topK: 5
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const embModels = EMBEDDING_MODELS[form.embeddingProvider] || [];
  const qaModels = (providers?.providers?.find(p => p.id === form.qaProvider)?.models || []).map(m => ({ value: m.id, label: m.id }));

  return (
    <ModalOverlay onClose={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'var(--bg-2)', border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)', padding: 28,
        width: 'min(560px, 100%)', maxHeight: 'calc(100dvh - 40px)',
        overflowY: 'auto', animation: 'fadeIn 0.2s ease',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18 }}>{t('modalTitle', { ns: 'instances' })}</h2>
            <p style={{ color: 'var(--text-3)', fontSize: 12, marginTop: 4 }}>{t('modalSubtitle', { ns: 'instances' })}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        <Input label={t('name', { ns: 'instances' })} value={form.name} onChange={e => set('name', e.target.value)} placeholder={t('namePlaceholder', { ns: 'instances' })} />
        <Input label={t('description', { ns: 'instances' })} value={form.description} onChange={e => set('description', e.target.value)} placeholder={t('descriptionPlaceholder', { ns: 'instances' })} rows={2} />

        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0', paddingTop: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 12, fontWeight: 700, letterSpacing: '1px' }}>⚡ EMBEDDING</div>
          <div className="grid-form-2">
            <Select label={t('embeddingProvider', { ns: 'instances' })}
              value={form.embeddingProvider}
              onChange={e => { set('embeddingProvider', e.target.value); set('embeddingModel', EMBEDDING_MODELS[e.target.value]?.[0] || ''); }}
              options={[{ value: 'openai', label: 'OpenAI' }, { value: 'ollama', label: 'Ollama (Local)' }]}
            />
            <Select label={t('embeddingModel', { ns: 'instances' })}
              value={form.embeddingModel}
              onChange={e => set('embeddingModel', e.target.value)}
              options={embModels.map(m => ({ value: m, label: m }))}
            />
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0', paddingTop: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--accent2)', marginBottom: 12, fontWeight: 700, letterSpacing: '1px' }}>🤖 Q&A LLM</div>
          <div className="grid-form-2">
            <Select label={t('qaProvider', { ns: 'instances' })}
              value={form.qaProvider}
              onChange={e => set('qaProvider', e.target.value)}
              options={[{ value: 'openai', label: 'OpenAI' }, { value: 'ollama', label: 'Ollama (Local)' }]}
            />
            <Select label={t('qaModel', { ns: 'instances' })}
              value={form.qaModel}
              onChange={e => set('qaModel', e.target.value)}
              options={qaModels.length > 0 ? qaModels : [{ value: form.qaModel, label: form.qaModel }]}
            />
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', margin: '16px 0', paddingTop: 16 }}>
          <div style={{ fontSize: 11, color: 'var(--yellow)', marginBottom: 12, fontWeight: 700, letterSpacing: '1px' }}>✂️ CHUNKING</div>
          <div className="grid-form-3">
            <Input label={t('chunkSize', { ns: 'instances' })} type="number" value={form.chunkSize} onChange={e => set('chunkSize', +e.target.value)} />
            <Input label={t('chunkOverlap', { ns: 'instances' })} type="number" value={form.chunkOverlap} onChange={e => set('chunkOverlap', +e.target.value)} />
            <Input label={t('topK', { ns: 'instances' })} type="number" value={form.topK} onChange={e => set('topK', +e.target.value)} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <Button variant="secondary" onClick={onClose}>{t('cancel', { ns: 'common' })}</Button>
          <Button onClick={() => { if (!form.name) return toast.error(t('nameRequired', { ns: 'instances' })); onSave(form); }}>
            {t('create', { ns: 'common' })}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}

export default function Instances() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const { t } = useTranslation(['instances', 'common', 'nav']);

  const { data, isLoading } = useQuery({ queryKey: ['instances'], queryFn: instancesApi.list });

  const createMut = useMutation({
    mutationFn: instancesApi.create,
    onSuccess: () => { qc.invalidateQueries(['instances']); setShowModal(false); toast.success(t('created', { ns: 'instances' })); }
  });

  const deleteMut = useMutation({
    mutationFn: instancesApi.delete,
    onSuccess: () => { qc.invalidateQueries(['instances']); toast.success(t('deleted', { ns: 'instances' })); }
  });

  const instances = data?.instances || [];

  return (
    <div className="animate-fade">
      <PageHeader
        title={t('instances', { ns: 'nav' })}
        subtitle={t('instancesCount', { ns: 'instances', count: instances.length })}
        actions={<Button icon={Plus} onClick={() => setShowModal(true)}>{t('new', { ns: 'instances' })}</Button>}
      />

      <div className="page-content">
        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
        ) : instances.length === 0 ? (
          <EmptyState
            icon={Database}
            title={t('noInstances', { ns: 'instances' })}
            description={t('noInstancesDesc', { ns: 'instances' })}
            action={<Button icon={Plus} onClick={() => setShowModal(true)}>{t('create', { ns: 'common' })}</Button>}
          />
        ) : (
          <div className="grid-cards">
            {instances.map(inst => (
              <Card key={inst.id} style={{ position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 8, flexShrink: 0,
                      background: 'linear-gradient(135deg, var(--accent), var(--accent2))',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      <Database size={16} color="white" />
                    </div>
                    <div>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--text-1)' }}>{inst.name}</div>
                      <div
                        title={t('copyIdTooltip', { ns: 'common' })}
                        onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(inst.id); toast.success(t('idCopied', { ns: 'common' })); }}
                        style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)', cursor: 'pointer', marginTop: 3, display: 'flex', alignItems: 'center', gap: 4 }}
                      >
                        <span>ID: {inst.id}</span>
                        <span style={{ opacity: 0.5 }}>⧉</span>
                      </div>
                      {inst.description && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{inst.description}</div>}
                    </div>
                  </div>
                </div>

                {/* Config badges */}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 16 }}>
                  <Badge color="blue">{inst.qaProvider}/{inst.qaModel}</Badge>
                  <Badge color="cyan">{inst.embeddingModel?.split('-').slice(0, 3).join('-')}</Badge>
                </div>

                {/* Stats */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: t('docs', { ns: 'instances' }), val: inst.stats?.documents || 0 },
                    { label: t('chunks', { ns: 'instances' }), val: inst.stats?.chunks || 0 },
                    { label: t('queries', { ns: 'instances' }), val: inst.stats?.queries || 0 }
                  ].map(s => (
                    <div key={s.label} style={{ background: 'var(--bg-3)', borderRadius: 6, padding: '8px', textAlign: 'center' }}>
                      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 18, color: 'var(--accent)' }}>{s.val}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button variant="secondary" size="sm" icon={Settings} style={{ flex: 1 }} onClick={() => navigate(`/instances/${inst.id}`)}>
                    {t('manage', { ns: 'common' })}
                  </Button>
                  <Button variant="secondary" size="sm" icon={MessageSquare} onClick={() => navigate(`/qa/${inst.id}`)}>
                    {t('qa', { ns: 'nav' })}
                  </Button>
                  <Button variant="danger" size="sm" icon={Trash2} onClick={() => {
                    if (window.confirm(t('deleteConfirm', { ns: 'instances', name: inst.name }))) deleteMut.mutate(inst.id);
                  }} />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <InstanceModal
          onClose={() => setShowModal(false)}
          onSave={data => createMut.mutate(data)}
        />
      )}
    </div>
  );
}

